import { useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { RoleSubscriptionEnum } from "context/SubscriptionProvider";

// Subscription role arrays
const SUBSCRIPTION_VIEWER_AND_ABOVE = [
  RoleSubscriptionEnum.subscription_viewer,
  RoleSubscriptionEnum.subscription_editor,
  RoleSubscriptionEnum.subscription_admin
];

const SUBSCRIPTION_EDITOR_AND_ABOVE = [
  RoleSubscriptionEnum.subscription_editor,
  RoleSubscriptionEnum.subscription_admin
];

const SUBSCRIPTION_ADMIN_ONLY = [RoleSubscriptionEnum.subscription_admin];

/**
 * Maps subscription-level routes to their required roles
 */
const SUBSCRIPTION_PAGE_ROLE_MAPPING: Record<string, RoleSubscriptionEnum[]> = {
  "/users": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/companies": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/projects": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/analytics": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/analytics/export": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/reports": SUBSCRIPTION_EDITOR_AND_ABOVE,
  "/reports/audit-reports": SUBSCRIPTION_EDITOR_AND_ABOVE,
  "/reports/audit-reports/activity-log": SUBSCRIPTION_EDITOR_AND_ABOVE,
  "/reports/printView": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/settings": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/integrations": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules/designpackages": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules/bidpackages": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules/materials": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules/designscope": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules/designscope/level-of-detail": SUBSCRIPTION_ADMIN_ONLY,
  "/settings/modules/designscope/bootstrap": SUBSCRIPTION_ADMIN_ONLY,
  "/home": SUBSCRIPTION_VIEWER_AND_ABOVE,
  "/new-project": SUBSCRIPTION_EDITOR_AND_ABOVE,
  "/change_password": SUBSCRIPTION_VIEWER_AND_ABOVE
};

/**
 * Routes that don't require subscription role validation
 */
const PUBLIC_ROUTES = [
  "/",
  "/Login",
  "/callback",
  "/subscription-select",
  "/set_password",
  "/reset_password",
  "/forgot_password",
  "/reset_expired_password",
  "/account-locked",
  "/accept-invite",
  "/submit-lead-time",
  "/admin"
];

/**
 * Hook to validate user access to subscription-level routes based on their subscription role
 */
function useValidateSubscriptionRouteAccess({
  subscriptionRole
}: {
  subscriptionRole: RoleSubscriptionEnum | undefined;
}): boolean {
  const { pathname } = useLocation();

  return useMemo(() => {
    // Check if it's a public route - always allow access
    const isPublicRoute = PUBLIC_ROUTES.some((route) =>
      matchPath(pathname, {
        path: route,
        exact: true,
        strict: false
      })
    );
    if (isPublicRoute) {
      return true;
    }

    // Check if it's a project route - skip subscription validation for project routes
    if (pathname.startsWith("/project/")) {
      return true;
    }

    // If no subscription role is set, deny access to subscription-level routes
    if (
      subscriptionRole === undefined ||
      subscriptionRole === RoleSubscriptionEnum.none
    ) {
      return false;
    }

    // Find the matching route pattern and check role permissions
    const matchedRoute = Object.keys(SUBSCRIPTION_PAGE_ROLE_MAPPING).find(
      (route) =>
        matchPath(pathname, {
          path: route,
          exact: true,
          strict: false
        })
    );

    if (matchedRoute) {
      const allowedRoles = SUBSCRIPTION_PAGE_ROLE_MAPPING[matchedRoute];
      return allowedRoles.includes(subscriptionRole);
    }

    // If route doesn't match any defined pattern, deny access
    return false;
  }, [pathname, subscriptionRole]);
}

export default useValidateSubscriptionRouteAccess;
